<template>
  <div class="print">
    <!-- 1. 헤더 및 결재란 -->
    <section class="section-approval">
      <div class="header-left-area">
        <p class="print-title">{{ $t("작업중지제") }}</p>
        <div class="division-title mt-10">□ {{ $t("기본정보") }}</div>
      </div>

      <table class="table-approval-field mt-auto">
        <colgroup>
          <col width="38" />
          <col width="112" />
          <col width="112" />
          <col width="112" />
          <col width="112" />
        </colgroup>
        <tr>
          <th rowspan="2" class="gray p-0">
            <div class="flex justify-center items-center">
              <p style="writing-mode: vertical-lr">{{ $i18n.locale === "ko" ? $t("결&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;재") : $t("결재") }}</p>
            </div>
          </th>
          <th class="gray">{{ $t("작성자") }}</th>
          <th class="gray">{{ $t("검토자") }}</th>
          <th class="gray">{{ $t("회람자") }}</th>
          <th class="gray">{{ $t("승인자") }}</th>
        </tr>
        <tr>
          <td style="vertical-align: middle; height: 100px">
            <div class="signature-container">
              <img v-if="form.sign1.sign" :src="form.sign1.sign" class="signature-image" />
              <p v-else-if="form.sign1.name" class="name-text">{{ form.sign1.name }}</p>
            </div>
          </td>
          <td style="vertical-align: middle; height: 100px">
            <div class="signature-container">
              <img v-if="form.sign2.sign" :src="form.sign2.sign" class="signature-image" />
              <p v-else-if="form.sign2.name" class="name-text">{{ form.sign2.name }}</p>
            </div>
          </td>
          <td style="vertical-align: middle; height: 100px">
            <div class="signature-container">
              <img v-if="form.sign3.sign" :src="form.sign3.sign" class="signature-image" />
              <p v-else-if="form.sign3.name" class="name-text">{{ form.sign3.name }}</p>
            </div>
          </td>
          <td style="vertical-align: middle; height: 100px">
            <div class="signature-container">
              <img v-if="form.sign4.sign" :src="form.sign4.sign" class="signature-image" />
              <p v-else-if="form.sign4.name" class="name-text">{{ form.sign4.name }}</p>
            </div>
          </td>
        </tr>
      </table>
    </section>

    <!-- 2. 기본 정보 및 요청자 구분 -->
    <section class="section-stdInfo mb-10">
      <table class="w-full">
        <colgroup>
          <col width="140" />
          <col />
          <col width="140" />
          <col />
        </colgroup>
        <tr>
          <th>{{ $t("회사명") }}</th>
          <td>
            <div class="text-left px-2">{{ form.stdInfo.field_name }}</div>
          </td>
          <th>{{ $t("작성자") }}</th>
          <td>
            <div class="text-left px-2">{{ form.stdInfo.writer_name }}</div>
          </td>
        </tr>
        <tr>
          <th>{{ $t("작성일") }}</th>
          <td>
            <div class="text-left px-2">{{ form.stdInfo.date }}</div>
          </td>
          <th>{{ $t("요청자 구분") }}</th>
          <td>
            <div class="flex justify-center gap-4">
              <PrintCheckbox :label="$t('사업주')" :value="form.type === '사업주'" />
              <PrintCheckbox :label="$t('근로자')" :value="form.type === '근로자'" />
              <PrintCheckbox :label="$t('기타')" :value="form.type === '기타'" />
            </div>
          </td>
        </tr>
      </table>
    </section>

    <!-- 3. 작업중지 및 사용중지 현황 -->
    <section class="section-request mb-10">
      <div class="division-title">□ {{ $t("작업중지 및 사용중지 현황") }}</div>
      <table class="w-full">
        <colgroup>
          <col width="160" />
          <col />
          <col width="160" />
          <col />
        </colgroup>
        <tr>
          <th>{{ $t("요청자") }}</th>
          <td>{{ form.inputC_4 }}</td>
          <th>{{ $t("중지대상") }}</th>
          <td>{{ form.inputC_5 }}</td>
        </tr>
        <tr>
          <th>{{ $t("재해형태") }}</th>
          <td style="color: red">{{ $t(form.inputC_6) }}</td>
          <th>{{ $t("중지일시") }}</th>
          <td>{{ form.inputC_8 }}</td>
        </tr>
        <tr>
          <th>{{ $t("점검일") }}</th>
          <td>{{ form.inputC_9 }}</td>
          <th>{{ $t("점검장소") }}</th>
          <td>{{ form.inputC_10 }}</td>
        </tr>
        <tr>
          <th>{{ $t("중지사유") }}</th>
          <td colspan="3" class="text-left p-4 whitespace-pre-wrap">{{ form.inputC_7 }}</td>
        </tr>
      </table>
    </section>

    <!-- 4. 조치 현황 -->
    <section class="section-action mb-10">
      <div class="division-title">□ {{ $t("조치 현황") }}</div>
      <table class="w-full">
        <colgroup>
          <col width="160" />
          <col />
          <col width="160" />
          <col />
        </colgroup>
        <tr>
          <th>{{ $t("조치자") }}</th>
          <td>{{ form.inputE_3 }}</td>
          <th>{{ $t("조치 완료일") }}</th>
          <td>{{ form.inputE_6 }}</td>
        </tr>
        <tr>
          <th>{{ $t("조치 내용") }}</th>
          <td colspan="3" class="text-left p-4 whitespace-pre-wrap">{{ form.inputE_4 }}</td>
        </tr>
      </table>
    </section>

    <!-- 5. 조치 전 사진 -->
    <section class="section-photo mb-10 page-break" v-if="form.photoList_1 && form.photoList_1.length > 0">
      <div class="division-title">□ {{ $t("조치 전 사진") }}</div>
      <table class="w-full">
        <colgroup>
          <col width="100" />
          <col />
        </colgroup>
        <template v-for="(photo, index) in form.photoList_1">
          <tr :key="'before_photo_' + index">
            <td colspan="2" style="height: 450px">
              <img v-if="photo.path" :src="photo.path" class="max-w-full h-full object-contain mx-auto block" />
            </td>
          </tr>
          <tr :key="'before_desc_' + index">
            <th>{{ $t("내용") }}</th>
            <td class="text-left">{{ photo.input }}</td>
          </tr>
        </template>
      </table>
    </section>

    <!-- 6. 조치 후 사진 -->
    <section class="section-photo mb-10 page-break" v-if="form.photoList_2 && form.photoList_2.length > 0">
      <div class="division-title">□ {{ $t("조치 후 사진") }}</div>
      <table class="w-full">
        <colgroup>
          <col width="100" />
          <col />
        </colgroup>
        <template v-for="(photo, index) in form.photoList_2">
          <tr :key="'after_photo_' + index">
            <td colspan="2" style="height: 450px">
              <img v-if="photo.path" :src="photo.path" class="max-w-full h-full object-contain mx-auto block" />
            </td>
          </tr>
          <tr :key="'after_desc_' + index">
            <th>{{ $t("내용") }}</th>
            <td class="text-left">{{ photo.input }}</td>
          </tr>
        </template>
      </table>
    </section>

    <!-- 7. 파일첨부 -->
    <section class="section-file mb-10" v-if="form.fileList && form.fileList.length > 0">
      <div class="division-title">□ {{ $t("첨부파일") }}</div>
      <table class="w-full">
        <colgroup>
          <col width="160" />
          <col />
        </colgroup>
        <tr v-for="(file, index) in form.fileList" :key="index">
          <th>{{ $t("파일명") }}</th>
          <td class="text-left">{{ file.fileName }}</td>
        </tr>
      </table>
    </section>
  </div>
</template>

<script>
import PrintCheckbox from "@/components/print/PrintCheckbox.vue";

export default {
  name: "Print",
  components: { PrintCheckbox },
  props: {
    isPrint: Boolean,
    board: Object,
    form: Object,
  },
};
</script>

<style lang="scss" scoped>
.print {
  .print-title {
    font-size: 32px;
    font-weight: bold;
    color: #000;
    text-align: center;
    padding: 20px 0;
  }
  .division-title {
    font-size: 24px;
    font-weight: bold;
    color: #3c3c3c;
    padding: 1.25rem 0rem;
  }

  .signature-container {
    width: 100%;
    height: 100px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 10px;

    .signature-image {
      width: auto;
      height: auto;
      max-width: 100%;
      max-height: 80px;
      object-fit: contain;
      display: block;
    }
    .name-text {
      font-size: 16px;
      margin-top: 5px;
    }
  }

  table {
    table-layout: fixed;
    border-collapse: collapse;
    width: 100%;

    th,
    td {
      border: 1px solid #757575;
      text-align: center;
      font-size: 18px;
      padding: 10px;
      vertical-align: middle;
    }
    th {
      background-color: #ececec;
      font-weight: 500;
    }
    td {
      word-break: break-all;
    }
    .gray {
      background-color: #ececec;
    }
    .text-left {
      text-align: left;
      padding: 10px 14px;
      word-break: break-word;
    }
  }

  .section-approval {
    display: grid;
    grid-template-columns: 1fr auto;
    width: 100%;
    align-items: flex-start;

    .header-left-area {
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      height: 100%;
      padding-top: 20px;
    }

    .table-approval-field {
      width: auto;
      margin-left: 10px;
      tr:last-child {
        th,
        td {
          border-bottom: 1px solid #757575;
        }
      }
    }
  }
}

@media print {
  section {
    page-break-inside: avoid;
    page-break-after: auto;
  }
  tr {
    page-break-inside: avoid;
    page-break-after: auto;
  }
  .page-break {
    page-break-inside: avoid;
    page-break-after: auto;
  }
}
</style>
